import React, { useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { XIcon, CheckCircleIcon, ExclamationCircleIcon } from '@heroicons/react/outline';
import { functions } from '../../firebase';
import { httpsCallable } from 'firebase/functions';
import { TRIAGE_FORM_KEYS } from '../utils/referralStatus';

/**
 * AssignTriageFormsDialog
 * -----------------------
 * Purpose:
 *   A modal dialog that allows Legal Guardians to self-assign their referral
 *   triage forms after completing their initial referral forms and receiving
 *   the Strengths & Support Navigator results.
 *
 * Business context:
 *   After a referral has been submitted and the SSN has been generated, guardians
 *   can choose to provide additional information through 7 comprehensive forms.
 *   This information is used by clinicians during the triage and assessment
 *   initiation process to better understand the child's history and needs.
 *
 * Flow:
 *   1. Guardian clicks "Provide additional information" button on patient overview
 *   2. Dialog opens explaining what forms will be assigned and their purpose
 *   3. On confirmation, a Cloud Function is triggered to assign the forms
 *   4. Success state is shown with confirmation message
 *   5. User can dismiss the dialog; page will reflect the new forms
 *
 * Props:
 *   - isOpen: boolean - Controls dialog visibility
 *   - onOpenChange: (open: boolean) => void - Callback when dialog should open/close
 *   - patientRecordId: string - The CYP's patient record ID for form assignment
 *   - onSuccess: () => void - Optional callback after successful assignment
 */

// Display metadata for the triage forms. The canonical list of which forms
// exist is the single source `TRIAGE_FORM_KEYS` in referralStatus.js (TD-22);
// this map only supplies titles/descriptions keyed by those same keys.
const TRIAGE_FORM_META = {
  theirEarlyLife: { title: 'Their Early Life', description: 'Developmental history and milestones' },
  theirFamilyAndHomeLife: { title: 'Their Family and Home Life', description: 'Family structure and home environment' },
  theirLifestyle: { title: 'Their Lifestyle', description: 'Daily routines, diet, sleep, and activities' },
  theirHistoryWithTheLaw: { title: 'Their History with the Law', description: 'Any involvement with police or justice system' },
  theirFamilyHistory: { title: 'Their Family History', description: 'Family medical and mental health background' },
  theirMedicalHistory: { title: 'Their Medical History', description: 'Physical health, medications, and allergies' },
  theirMentalHealthHistory: { title: 'Their Mental Health History', description: 'Mental health diagnoses and support' },
};

const TRIAGE_FORMS = TRIAGE_FORM_KEYS.map((key) => ({
  key,
  title: TRIAGE_FORM_META[key]?.title || key,
  description: TRIAGE_FORM_META[key]?.description || '',
}));

export default function AssignTriageFormsDialog({ 
  isOpen, 
  onOpenChange, 
  patientRecordId,
  onSuccess 
}) {
  // UI state machine: 'confirm' → 'loading' → 'success' | 'error'
  const [state, setState] = useState('confirm');
  const [errorMessage, setErrorMessage] = useState(null);

  /**
   * Handles the assignment of triage forms by calling the backend Cloud Function.
   * The CF will create form instances for each triage form and mark them as
   * available for the guardian to complete.
   */
  const handleConfirm = async () => {
    setState('loading');
    setErrorMessage(null);

    try {
      // Call the Cloud Function to assign triage forms to this guardian
      // The CF will: 1) Validate the guardian's access, 2) Create form instances,
      // 3) Update the patient record to reflect triage forms are assigned
      const assignTriageForms = httpsCallable(functions, 'rmsAssignTriageForms');
      
      const result = await assignTriageForms({
        patientRecordId,
        formKeys: TRIAGE_FORMS.map(f => f.key),
      });

      if (result?.data?.success) {
        setState('success');
        // Optionally notify parent component of successful assignment
        if (onSuccess) {
          onSuccess();
        }
      } else {
        throw new Error(result?.data?.message || 'Failed to assign forms');
      }
    } catch (error) {
      console.error('Error assigning triage forms:', error);
      setErrorMessage(
        error?.message || 'Something went wrong. Please try again or contact support.'
      );
      setState('error');
    }
  };

  /**
   * Resets the dialog state when closing, so reopening starts fresh.
   */
  const handleOpenChange = (open) => {
    if (!open) {
      // Reset state after a short delay to avoid visual jump during close animation
      setTimeout(() => {
        setState('confirm');
        setErrorMessage(null);
      }, 200);
    }
    onOpenChange(open);
  };

  return (
    <Dialog.Root open={isOpen} onOpenChange={handleOpenChange}>
      <Dialog.Portal>
        {/* Backdrop overlay */}
        <Dialog.Overlay className="radix-dialog-overlay-motion fixed inset-0 bg-black/40 backdrop-blur-sm z-50" />
        
        {/* Dialog content container */}
        <Dialog.Content className="radix-dialog-content-centered-motion fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 max-h-[85vh] w-[90vw] max-w-[550px] bg-white rounded-xl shadow-lg z-50 overflow-hidden flex flex-col">
          
          {/* Header with close button */}
          <div className="flex items-start justify-between p-6 pb-0">
            <div>
              <Dialog.Title className="text-xl font-semibold text-gray-900">
                {state === 'success' 
                  ? 'Forms Assigned Successfully' 
                  : 'Provide Additional Information'}
              </Dialog.Title>
              {state === 'confirm' && (
                <Dialog.Description className="text-sm text-gray-600 mt-1">
                  Help clinicians understand your child better
                </Dialog.Description>
              )}
            </div>
            <Dialog.Close asChild>
              <button
                type="button"
                className="p-1 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
                aria-label="Close dialog"
              >
                <XIcon className="w-5 h-5" />
              </button>
            </Dialog.Close>
          </div>

          {/* Scrollable content area */}
          <div className="flex-grow overflow-y-auto p-6" style={{ maxHeight: '60vh' }}>
            
            {/* ─── Confirmation State ─────────────────────────────────────────── */}
            {state === 'confirm' && (
              <>
                {/* Explanation of what happens next */}
                <div className="bg-blue-50 border border-blue-100 rounded-lg p-4 mb-5">
                  <p className="text-sm text-blue-800">
                    To help with the assessment and triage process, you can provide 
                    additional information about your child's background and history. 
                    This information will be reviewed by clinicians to ensure they have 
                    a complete picture before the assessment begins.
                  </p>
                </div>

                {/* List of forms that will be assigned */}
                <h4 className="text-sm font-semibold text-gray-800 mb-3">
                  The following forms will be added to your to-do list:
                </h4>
                <ul className="space-y-2 mb-5">
                  {TRIAGE_FORMS.map((form) => (
                    <li 
                      key={form.key} 
                      className="flex items-start gap-3 p-3 rounded-lg bg-gray-50 border border-gray-100"
                    >
                      <div className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center mt-0.5">
                        <svg className="w-3.5 h-3.5 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                      </div>
                      <div>
                        <div className="text-sm font-medium text-gray-900">{form.title}</div>
                        <div className="text-xs text-gray-500">{form.description}</div>
                      </div>
                    </li>
                  ))}
                </ul>

                {/* Privacy/data usage note */}
                <div className="bg-amber-50 border border-amber-100 rounded-lg p-3">
                  <p className="text-xs text-amber-800">
                    <strong>Note:</strong> All information you provide is kept confidential 
                    and is only shared with the clinical team involved in your child's care. 
                    You can take as long as you need to complete these forms.
                  </p>
                </div>
              </>
            )}

            {/* ─── Loading State ─────────────────────────────────────────────── */}
            {state === 'loading' && (
              <div className="flex flex-col items-center justify-center py-10">
                {/* Spinner animation */}
                <div className="relative w-12 h-12 mb-4">
                  <div className="absolute inset-0 border-4 border-primary/20 rounded-full" />
                  <div className="absolute inset-0 border-4 border-transparent border-t-primary rounded-full animate-spin" />
                </div>
                <p className="text-sm text-gray-600">Assigning forms...</p>
                <p className="text-xs text-gray-400 mt-1">This should only take a moment</p>
              </div>
            )}

            {/* ─── Success State ─────────────────────────────────────────────── */}
            {state === 'success' && (
              <div className="flex flex-col items-center justify-center py-6">
                {/* Success icon with animation */}
                <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mb-4">
                  <CheckCircleIcon className="w-10 h-10 text-green-600" />
                </div>
                <p className="text-base font-medium text-gray-900 text-center mb-2">
                  7 forms have been added to your to-do list
                </p>
                <p className="text-sm text-gray-600 text-center max-w-sm">
                  You can complete these forms at your own pace. They will appear in 
                  your referral overview alongside your other forms.
                </p>
              </div>
            )}

            {/* ─── Error State ───────────────────────────────────────────────── */}
            {state === 'error' && (
              <div className="flex flex-col items-center justify-center py-6">
                {/* Error icon */}
                <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mb-4">
                  <ExclamationCircleIcon className="w-10 h-10 text-red-600" />
                </div>
                <p className="text-base font-medium text-gray-900 text-center mb-2">
                  Something went wrong
                </p>
                <p className="text-sm text-red-600 text-center max-w-sm mb-4">
                  {errorMessage}
                </p>
                <button
                  type="button"
                  onClick={() => setState('confirm')}
                  className="text-sm text-primary hover:text-primary/80 underline"
                >
                  Try again
                </button>
              </div>
            )}
          </div>

          {/* Footer with action buttons */}
          <div className="flex justify-end gap-3 p-6 pt-4 border-t border-gray-100">
            {state === 'confirm' && (
              <>
                <Dialog.Close asChild>
                  <button
                    type="button"
                    className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-sm font-medium"
                  >
                    Not now
                  </button>
                </Dialog.Close>
                <button
                  type="button"
                  onClick={handleConfirm}
                  className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors text-sm font-medium"
                >
                  Assign these forms
                </button>
              </>
            )}

            {state === 'loading' && (
              <button
                type="button"
                disabled
                className="px-4 py-2 bg-gray-100 text-gray-400 rounded-lg text-sm font-medium cursor-not-allowed"
              >
                Please wait...
              </button>
            )}

            {(state === 'success' || state === 'error') && (
              <Dialog.Close asChild>
                <button
                  type="button"
                  className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors text-sm font-medium"
                >
                  {state === 'success' ? 'Done' : 'Close'}
                </button>
              </Dialog.Close>
            )}
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
